import React, { useCallback, useEffect, useRef, useState } from "react";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];

type ImageDropzoneProps = {
  previewUrl: string | null;
  fileName?: string | null;
  enabled?: boolean;
  onSelect: (file: File) => void;
  onClear?: () => void;
  hint?: string;
};

const isAcceptedImage = (file: File | null | undefined): file is File =>
  Boolean(file && ACCEPTED_TYPES.includes(file.type));

const ImageDropzone = ({
  previewUrl,
  fileName,
  enabled = true,
  onSelect,
  onClear,
  hint,
}: ImageDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyFile = useCallback(
    (file: File | null | undefined) => {
      if (!enabled || !file) {
        return;
      }
      if (!isAcceptedImage(file)) {
        setError("PNG, JPG, WEBP, SVG 이미지만 업로드할 수 있습니다.");
        return;
      }
      setError(null);
      onSelect(file);
    },
    [enabled, onSelect]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onPaste = (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;
      const items = clipboardEvent.clipboardData?.items;
      if (!items) {
        return;
      }

      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            clipboardEvent.preventDefault();
            applyFile(file);
            return;
          }
        }
      }
    };

    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [applyFile, enabled]);

  return (
    <div className="image-dropzone-wrap">
      <div
        className={`image-dropzone ${dragging ? "is-dragging" : ""} ${previewUrl ? "has-preview" : ""}`}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragging(false);
          applyFile(event.dataTransfer.files?.[0]);
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          className="image-dropzone__input"
          type="file"
          accept={ACCEPTED_TYPES.join(",")}
          onChange={(event) => {
            applyFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />

        {previewUrl ? (
          <div className="image-dropzone__preview">
            <div className="image-dropzone__thumb">
              <img src={previewUrl} alt="로고 미리보기" />
            </div>
            <div className="image-dropzone__preview-meta">
              <strong>{fileName || "선택된 이미지"}</strong>
              <span>클릭하거나 새 이미지를 드롭해서 교체</span>
            </div>
          </div>
        ) : (
          <div className="image-dropzone__empty">
            <span className="image-dropzone__icon" aria-hidden>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 16V4M12 4L7 9M12 4L17 9"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 16.5V18.2C4 19.1941 4.80589 20 5.8 20H18.2C19.1941 20 20 19.1941 20 18.2V16.5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <p className="image-dropzone__title">이미지를 여기에 드래그하세요</p>
            <p className="image-dropzone__desc">
              {hint || "클릭해서 파일 선택 · Ctrl/⌘ + V 붙여넣기 · PNG, JPG, WEBP, SVG"}
            </p>
          </div>
        )}
      </div>

      {(previewUrl || error) && (
        <div className="image-dropzone__footer">
          {error && <p className="image-dropzone__error">{error}</p>}
          {previewUrl && onClear && (
            <button
              className="image-dropzone__clear"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onClear();
              }}
            >
              이미지 제거
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageDropzone;
